import React, { useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import HomeAdd from '../../assets/icons/HomeAdd.svg';
import OfficeAdd from '../../assets/icons/OfficeAdd.svg';
import OtherAdd from '../../assets/icons/OtherAdd.svg';
import { useLocalization } from '../Context/MultiLanguageContext';

export interface Address {
  id: string;
  latitude: number;
  longitude: number;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  pinCode: string;
  addressType?: string; 
}

const openGoogleMaps = (latitude: number, longitude: number) => {
  const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
  window.open(url, "_blank");
};

interface MapAddressDetailsProps {
  addresses: Address[];
  onLocationChange: (id: string, latitude: number, longitude: number) => void;
}

const getAddressTypeIconHtml = (addressType: string) => {
  const type = addressType.toLowerCase();
  if (type === 'home') {
    return `<img src="${HomeAdd}" alt="Home" style="width:40px; height:40px;" />`;
  } else if (type === 'office') {
    return `<img src="${OfficeAdd}" alt="Office" style="width:40px; height:40px;" />`;
  } else {
    return `<img src="${OtherAdd}" alt="Other" style="width:40px; height:40px;" />`;
  }
};

const createCustomIcon = (addressType: string): L.DivIcon => {
  return L.divIcon({
    html: getAddressTypeIconHtml(addressType),
    iconSize: [60, 60],
    className: 'custom-div-icon' 
  });
};

const MapEditLocation: React.FC<MapAddressDetailsProps> = ({ addresses, onLocationChange }) => {
  const center: [number, number] = addresses.length > 0 
    ? [addresses[0].latitude, addresses[0].longitude] 
    : [9.9921, 76.3019];

  const handleDragEnd = (e: L.LeafletEvent, id: string) => {
    const newLat = e.target.getLatLng().lat;
    const newLng = e.target.getLatLng().lng;
    onLocationChange(id, newLat, newLng);
  };

  const { t } = useLocalization();
  
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        if (addresses.length > 0) {
          const address = addresses[0];
          onLocationChange(address.id, lat, lng);
        }
      },
    });
    return null;
  };

  return (
    <MapContainer center={center} zoom={17} className="h-full w-full">
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />

      <MapClickHandler />

      {addresses.map((address) => (
        <Marker
          key={address.id || `${address.latitude}-${address.longitude}`}
          position={[address.latitude, address.longitude]}
          icon={createCustomIcon(address.addressType || 'other')}
          draggable={true}
          eventHandlers={{
            dragend: (e) => handleDragEnd(e, address.id),
            // click: () => openGoogleMaps(address.latitude, address.longitude),
          }}
        >
          <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
            <span>{address.addressLine3}</span>
          </Tooltip>
          <Popup>
            <div>
              <strong>{t("address")}:</strong> {address.addressLine1}, {address.addressLine2}<br />
              <strong>{t("city")}:{address.addressLine3}</strong><br />
              <strong>{t("postalCode")}:</strong> {address.pinCode}<br />
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default MapEditLocation;
